/**
 * EmailHtmlRenderer — sanitized HTML email body rendered inside a sandboxed
 * iframe (`srcdoc` + `sandbox`) with Ando theming and auto-height.
 *
 * SOTA May 2026 pattern derived from Gmail web / Hey / Notion Mail / AdGuard
 * guidance:
 *
 *  1. **Defence-in-depth sanitisation** — even though the BE already strips
 *     `<script>/<style>/event handlers` via bleach, we re-run DOMPurify in
 *     the browser. Two filters, two failure modes, near-zero residual risk.
 *
 *  2. **Sandboxed iframe (`srcdoc`)** — the email HTML is injected into a
 *     null-origin iframe with `sandbox="allow-scripts allow-popups
 *     allow-popups-to-escape-sandbox"`. We deliberately allow scripts
 *     because:
 *       - we never grant `allow-same-origin`, so any residual user-script
 *         cannot touch Ando cookies / localStorage / parent DOM,
 *       - the only script we ourselves inject is hard-coded (height
 *         postMessage + link interception), not user-supplied.
 *
 *  3. **CSP meta tag** — additional belt-and-braces inside the iframe:
 *     `default-src 'none'; img-src https: data: cid:; style-src 'unsafe-
 *     inline'; font-src https: data:`. No remote scripts, no frames, no
 *     forms, no plugins.
 *
 *  4. **Privacy-first remote images** — by default `loadRemoteImages=false`.
 *     `<img>` with remote `src` is rewritten to a blocked-image placeholder
 *     SVG and tracking pixels (1x1 or zero-size) are counted/stripped. The
 *     caller is notified via `onTrackersDetected`.
 *
 *  5. **Auto-height** — the inner script posts `body.scrollHeight` whenever
 *     the body resizes. The wrapper iframe is sized to fit, capped at
 *     `maxHeight` if provided.
 *
 *  6. **Link interception** — `<a>` clicks post a `ando:email-link` message
 *     to the parent. The caller can hand the URL off to the Tauri shell
 *     (`opener` plugin) or analytics; default fallback opens in a new
 *     window with `noopener,noreferrer`.
 */

import DOMPurify, { type Config as DOMPurifyConfig } from "dompurify";
import { ShieldAlert } from "lucide-react";
import * as React from "react";
import { cn } from "../../lib/cn";

// ──────────────────────────────────────────────────────────────────────────────
// Public API
// ──────────────────────────────────────────────────────────────────────────────

export interface EmailHtmlRendererProps {
  /** Server-sanitized HTML (defence-in-depth: we re-sanitize FE-side too). */
  readonly html: string;
  /** Privacy: when false, remote images and tracking pixels are stripped. */
  readonly loadRemoteImages?: boolean;
  /** Optional max-height before scroll. Default unlimited (auto-fit). */
  readonly maxHeight?: number;
  /** Forward className to outer wrapper. */
  readonly className?: string;
  /** Fired when an `<a>` is clicked. Caller can shellOpen + analytics. */
  readonly onLinkClick?: (href: string) => void;
  /** Fired when tracking pixels are detected (count + sample URLs). */
  readonly onTrackersDetected?: (count: number, samples: readonly string[]) => void;
  /** Map of ``Content-ID`` → resolved URL (typically a blob URL produced
   *  by pre-fetching the inline attachment bytes through the auth-bearing
   *  API client). Used to rewrite ``<img src="cid:foo">`` in the body so
   *  the browser can actually load the image. The key MUST match the
   *  ``content_id`` value from the email parser (lowercase, no angle
   *  brackets — the renderer normalises both sides before lookup).
   *  Without this map the renderer leaves ``cid:`` srcs untouched and
   *  the browser renders the broken-image icon (Jun 20, 2026 fix). */
  readonly inlineImages?: Readonly<Record<string, string>>;
  /** Label of the blocked-trackers chip, already translated by the caller
   *  (this package has no i18n runtime). Receives the count so the caller
   *  can handle plurals in its own locale. Falls back to Italian. */
  readonly trackerLabel?: (count: number) => string;
  /** Accessible label of the same chip — it toggles the URL list, so it
   *  says more than the visible text. Falls back to Italian. */
  readonly trackerAriaLabel?: (count: number) => string;
}

/** Normalise a Content-ID for map lookup. Email parsers vary on
 *  whether they keep the surrounding angle brackets and on case;
 *  trimming both makes the map insensitive to those quirks. */
function normaliseCid(raw: string): string {
  return raw.trim().replace(/^<|>$/g, "").toLowerCase();
}

// ──────────────────────────────────────────────────────────────────────────────
// Sanitiser configuration
// ──────────────────────────────────────────────────────────────────────────────

const DOMPURIFY_CONFIG: DOMPurifyConfig = {
  ALLOWED_TAGS: [
    "a",
    "p",
    "b",
    "i",
    "em",
    "strong",
    "u",
    "s",
    "ul",
    "ol",
    "li",
    "table",
    "thead",
    "tbody",
    "tr",
    "td",
    "th",
    "img",
    "br",
    "hr",
    "blockquote",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "span",
    "div",
    "code",
    "pre",
    "figure",
    "figcaption",
    "picture",
    "source",
    // Tag presentazionali e semantici che i vari generatori di email
    // producono: Outlook/Word (``font``, ``center``, ``MsoNormal`` via
    // ``<style>``), i builder di newsletter (``section``/``header``) e i
    // client che emettono HTML5. La lista è volutamente generosa sui tag
    // inerti e chiusa su tutto ciò che può eseguire o inquadrare.
    "style",
    "center",
    "font",
    "colgroup",
    "col",
    "tfoot",
    "caption",
    "small",
    "big",
    "sub",
    "sup",
    "dl",
    "dt",
    "dd",
    "abbr",
    "address",
    "wbr",
    "del",
    "ins",
    "strike",
    "tt",
    "nobr",
    "q",
    "cite",
    "mark",
    "time",
    "bdi",
    "bdo",
    "samp",
    "kbd",
    "var",
    "section",
    "article",
    "header",
    "footer",
    "main",
    "aside",
    "nav",
  ],
  ALLOWED_ATTR: [
    "href",
    "src",
    "alt",
    "title",
    "target",
    "rel",
    "style",
    "class",
    "width",
    "height",
    "srcset",
    "sizes",
    // Attributi presentazionali dei layout tabellari: senza questi la
    // struttura delle newsletter (card, sfondi, spaziature) collassa.
    "bgcolor",
    "background",
    "align",
    "valign",
    "cellpadding",
    "cellspacing",
    "border",
    "colspan",
    "rowspan",
    "nowrap",
    "hspace",
    "vspace",
    "face",
    "color",
    "size",
    "start",
    "type",
    "scope",
    "headers",
    "dir",
    "lang",
    // Necessario perché i selettori del <style> dell'email risolvano.
    "id",
    "role",
  ],
  // Anchored allow-list — `javascript:`/`vbscript:`/`file:` etc. are
  // rejected because they do not match any of these prefixes.
  // NB: trailing `?` was deliberately removed (would have made the whole
  // match optional → permitted *any* URL including `javascript:`).
  ALLOWED_URI_REGEXP:
    /^(?:https?:|mailto:|tel:|cid:|data:image\/(?:png|jpe?g|gif|webp|svg\+xml);)/i,
  // DOMPurify applica ``ALLOWED_URI_REGEXP`` a OGNI attributo, non solo a
  // quelli che portano un URL: senza questa lista di attributi "inert",
  // la regex ancorata qui sopra scarta ``width="600"``, ``bgcolor="#fff"``,
  // ``align="center"`` e compagnia, e ogni layout tabellare collassa.
  // ``href``/``src``/``background`` restano fuori: quelli DEVONO passare
  // dalla regex.
  ADD_URI_SAFE_ATTR: [
    "width",
    "height",
    "srcset",
    "sizes",
    "target",
    "rel",
    "bgcolor",
    "align",
    "valign",
    "cellpadding",
    "cellspacing",
    "border",
    "colspan",
    "rowspan",
    "nowrap",
    "hspace",
    "vspace",
    "face",
    "color",
    "size",
    "start",
    "type",
    "scope",
    "headers",
    "dir",
    "lang",
  ],
  FORBID_TAGS: [
    "script",
    "iframe",
    "object",
    "embed",
    "meta",
    "base",
    "link",
    "form",
    "input",
    "button",
  ],
  FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover"],
  ADD_ATTR: ["target", "rel"],
  // Senza questo il parser sposta nell'``<head>`` uno ``<style>`` che apre
  // il documento e ``doc.body`` lo perde: il CSS dell'email sparirebbe.
  FORCE_BODY: true,
};

/**
 * Ripulisce il CSS di un `<style>` dell'email.
 *
 * Il tag è consentito (senza, le newsletter moderne perdono card, bordi e
 * spaziature) ma il CSS resta filtrato: l'iframe è già null-origin e senza
 * `allow-same-origin`, questo è il secondo strato.
 */
function sanitiseStyleCss(css: string, loadRemoteImages: boolean): string {
  let out = css.replace(/@import[^;]*;?/gi, "");
  out = out.replace(/expression\s*\(/gi, "void(");
  out = out.replace(/(?:-moz-)?binding\s*:[^;}]*/gi, "");
  out = out.replace(/behavior\s*:[^;}]*/gi, "");
  out = out.replace(
    /url\(\s*(['"]?)\s*(?:javascript|vbscript|data:text\/html)[^)]*\)/gi,
    "none",
  );
  // Un'email non può uscire dal proprio flusso e coprire la chrome di Ando.
  out = out.replace(/position\s*:\s*(?:fixed|sticky)/gi, "position:static");
  if (!loadRemoteImages) {
    out = out.replace(/url\(\s*['"]?https?:\/\/[^)]*\)/gi, "none");
  }
  return out;
}

/** GIF 1x1 trasparente: sostituisce il src remoto senza generare richieste. */
const BLOCKED_IMG_SRC =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

/** Tracking-pixel heuristic — 1x1 or zero-size hidden images. */
function looksLikeTrackingPixel(img: Element): boolean {
  const w = img.getAttribute("width");
  const h = img.getAttribute("height");
  if ((w === "1" && h === "1") || w === "0" || h === "0") return true;
  const style = img.getAttribute("style") || "";
  if (/(?:^|;)\s*(?:width|height)\s*:\s*0(?:px)?\s*(?:;|$)/i.test(style)) return true;
  if (/display\s*:\s*none/i.test(style) || /visibility\s*:\s*hidden/i.test(style)) return true;
  return false;
}

interface SanitisedResult {
  readonly html: string;
  readonly trackerCount: number;
  readonly trackerSamples: readonly string[];
}

/**
 * Run DOMPurify with our config and, depending on `loadRemoteImages`,
 * either keep remote `<img>` as-is or rewrite them to a placeholder.
 * Always detects tracking pixels and returns the count + sample URLs.
 *
 * Implementation note: we lean on DOMPurify hooks (`afterSanitizeAttributes`
 * + `uponSanitizeElement`) so we get a single-pass transform that benefits
 * from the same DOM walker — no second tree walk needed.
 */
function sanitiseEmail(
  rawHtml: string,
  loadRemoteImages: boolean,
  inlineImages: Readonly<Record<string, string>> = {},
): SanitisedResult {
  const trackerSamples: string[] = [];
  let trackerCount = 0;

  // `uponSanitizeElement` fires BEFORE attribute filtering, so we can read
  // raw `width`/`height` (which would otherwise get stripped by DOMPurify
  // before `afterSanitizeAttributes`) to detect tracking pixels. We still
  // need `afterSanitizeAttributes` for the remote-image placeholder swap
  // because that has to happen AFTER the URI allow-list normalises `src`.
  const preHook = (node: Node): void => {
    if (!(node instanceof Element)) return;
    if (node.tagName !== "IMG") return;
    if (looksLikeTrackingPixel(node)) {
      const src = node.getAttribute("src") || "";
      trackerCount += 1;
      if (trackerSamples.length < 5 && src) trackerSamples.push(src);
      // Mark for removal — DOMPurify will drop the element.
      node.remove();
    }
  };

  const postHook = (node: Element): void => {
    if (node.tagName === "STYLE") {
      node.textContent = sanitiseStyleCss(node.textContent || "", loadRemoteImages);
      return;
    }
    if (node.tagName === "A") {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer nofollow");
    }
    // ``srcset`` è URI-safe per DOMPurify (vedi ADD_URI_SAFE_ATTR) e
    // sfuggirebbe al blocco remote-images che sotto agisce solo su ``src``.
    if (!loadRemoteImages && node.hasAttribute("srcset")) {
      node.removeAttribute("srcset");
    }
    if (node.tagName === "IMG") {
      const src = node.getAttribute("src") || "";
      // Inline attachment (Jun 20, 2026): rewrite ``cid:foo`` srcs to
      // the pre-fetched blob URL the caller supplied. The browser can
      // load a blob URL natively (no auth headers needed); the BE
      // bytes have already been fetched through the auth-bearing API
      // client at the parent. Without this rewrite the browser
      // renders the broken-image icon and the user sees the filename
      // as alt text (the symptom that triggered this fix).
      if (/^cid:/i.test(src)) {
        const cid = normaliseCid(src.slice(4));
        const resolved = cid ? inlineImages[cid] : undefined;
        if (resolved) {
          node.setAttribute("data-original-src", src);
          node.setAttribute("src", resolved);
        }
        // If unresolved we leave the cid as-is. The browser still
        // can't load it (broken-image icon) — same UX as before the
        // fix, no regression — but the FE didn't silently swap to a
        // placeholder either, so the parent can surface a "missing
        // inline attachment" warning later if needed.
      } else if (!loadRemoteImages && /^https?:\/\//i.test(src)) {
        // Privacy mode. L'immagine collassa invece di lasciare un
        // placeholder alto quanto l'originale (era la causa dei vuoti
        // enormi nelle newsletter): il conteggio resta nel toggle
        // "Mostra immagini esterne", il layout resta compatto. Stessa
        // scelta di Gmail e Apple Mail.
        node.setAttribute("data-original-src", src);
        node.setAttribute("src", BLOCKED_IMG_SRC);
        node.removeAttribute("width");
        node.removeAttribute("height");
        node.setAttribute("style", `${node.getAttribute("style") || ""};display:none`);
      }
    }
  };

  DOMPurify.addHook("uponSanitizeElement", preHook);
  DOMPurify.addHook("afterSanitizeAttributes", postHook);
  let html = "";
  try {
    // The overload without `RETURN_TRUSTED_TYPE: true` returns a plain
    // string — we cast via `unknown` to disarm TS's TrustedHTML widening.
    html = DOMPurify.sanitize(rawHtml, DOMPURIFY_CONFIG) as unknown as string;
  } finally {
    // ALWAYS detach hooks — DOMPurify hooks are module-scoped and would
    // leak into unrelated sanitize() callers otherwise.
    DOMPurify.removeHook("uponSanitizeElement");
    DOMPurify.removeHook("afterSanitizeAttributes");
  }

  return { html, trackerCount, trackerSamples };
}

// ──────────────────────────────────────────────────────────────────────────────
// Ando-themed CSS reset (rendered inside the iframe)
// ──────────────────────────────────────────────────────────────────────────────

// Email-reading typography.
//
// Le regole tipografiche stanno dentro `:where()` → specificità 0: valgono
// come default quando l'email non dice nulla, e si fanno da parte appena
// l'email definisce il proprio stile. È il comportamento di Gmail: senza
// questo, il nostro arancione sui link e i nostri margini sui `<p>`
// vincerebbero sul design della newsletter.
//
// Regola d'oro (Gmail / Outlook web): NON si impone un box model al
// contenuto. Le email sono scritte a larghezza fissa (~600px) assumendo
// `content-box`; un `* { box-sizing: border-box; max-width: 100% }`
// comprime le tabelle invece di lasciarle scrollare, e `word-break:
// break-word` (alias di `overflow-wrap: anywhere`) porta la min-content
// width delle celle a un carattere — le due cose insieme spezzano il
// testo in verticale, una lettera per riga. Il body scrolla in
// orizzontale quando l'email è più larga del pane.
const ANDO_EMAIL_CSS_RESET = `
:root { color-scheme: light; }
html, body { margin: 0; padding: 0; }
body {
  padding: 16px 20px;
  font-family: Manrope, -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 15px;
  line-height: 1.65;
  color: #1F1D1A;
  background: transparent;
  overflow-wrap: break-word;
  -webkit-text-size-adjust: 100%;
}
:where(p) { margin: 0 0 16px 0; }
/* Link sobri: l'arancione è l'identità Ando, riservata all'interfaccia, non
   spesa sul contenuto di terzi. Sottolineatura in hover — su una newsletter
   fatta di soli link, quella continua era un muro. */
:where(a) { color: #1F1D1A; text-decoration: none; }
:where(a:hover) { color: #E8650E; text-decoration: underline; text-decoration-thickness: 1px; text-underline-offset: 2px; }
img { max-width: 100%; }
:where(img) { border-radius: 4px; }
img:not([height]) { height: auto; }
:where(blockquote) { border-left: 3px solid rgba(0,0,0,0.10); padding-left: 16px; margin: 16px 0; color: #56544F; }
:where(pre, code) { font-family: "JetBrains Mono", ui-monospace, monospace; font-size: 13px; background: rgba(0,0,0,0.04); padding: 1px 4px; border-radius: 3px; }
:where(pre) { padding: 10px 12px; overflow-x: auto; }
:where(h1,h2,h3,h4,h5,h6) { font-family: "Space Grotesk", system-ui, sans-serif; margin: 20px 0 10px 0; line-height: 1.3; }
:where(h1) { font-size: 22px; } :where(h2) { font-size: 19px; } :where(h3) { font-size: 17px; }
:where(hr) { border: 0; border-top: 1px solid rgba(0,0,0,0.06); margin: 20px 0; }
:where(ul, ol) { padding-left: 24px; margin: 0 0 16px 0; }
:where(li) { margin-bottom: 6px; }
::selection { background: rgba(255,122,26,0.25); }
`.trim();

/**
 * Inner-iframe bootstrap script. Hard-coded — never composed from user
 * input. Posts height updates + link click events to the parent.
 */
const IFRAME_BOOTSTRAP = `
(function(){
  // May 24, 2026 — Cross-browser robust height computation.
  // Picking only body.scrollHeight undercounts when the body has
  // overflow:visible and the html element actually owns the layout
  // (Safari/WebKit + Tauri WKWebView). Take the MAX of body/html
  // scrollHeight + offsetHeight so the iframe never clips its own
  // content — the parent motion.div handles ALL scrolling.
  var measure = function(){
    return Math.max(
      document.body.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.scrollHeight,
      document.documentElement.offsetHeight
    );
  };
  var lastSent = 0;
  var post = function(){
    try {
      var h = measure() + 16;
      // De-dupe: skip postMessage when the height is unchanged (saves
      // parent re-renders during ResizeObserver thrash).
      if (h === lastSent) return;
      lastSent = h;
      parent.postMessage({
        type: 'ando:email-height',
        height: h
      }, '*');
    } catch (e) { /* parent may be cross-origin in tests */ }
  };
  // Initial emit ASAP (DOMContentLoaded fires before window.load).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', post);
  } else {
    post();
  }
  // Post-paint emit — guarantees the first measurement runs AFTER the
  // browser has applied CSS layout (otherwise scrollHeight may be 0).
  if (typeof requestAnimationFrame !== 'undefined') {
    requestAnimationFrame(function(){ requestAnimationFrame(post); });
  }
  // Standard load (images + sub-resources).
  window.addEventListener('load', post);
  // Font-load reflow — typography (Manrope/Space Grotesk) usually
  // arrives after first paint and shifts line-heights, growing the
  // body by 10-30%. document.fonts.ready resolves only when ALL
  // declared fonts have loaded.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(post);
  }
  // Aggressive polling for the first 3s — covers (a) late images
  // resolving from cache after load, (b) ResizeObserver not firing in
  // some sandboxed null-origin contexts (Tauri WKWebView edge case),
  // (c) reflow caused by lazy CSS @media kicking in after first paint.
  var pollIntervals = [50, 100, 200, 400, 800, 1200, 1800, 2500, 3000];
  pollIntervals.forEach(function(ms){ setTimeout(post, ms); });
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(post).observe(document.body);
    new ResizeObserver(post).observe(document.documentElement);
  }
  // MutationObserver — catch DOM mutations (e.g. parent toggling
  // loadRemoteImages swaps the srcdoc and remounts; for in-place
  // src changes within the SAME srcdoc this picks them up).
  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(post).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true
    });
  }
  document.querySelectorAll('a[href]').forEach(function(a){
    a.addEventListener('click', function(e){
      e.preventDefault();
      try {
        parent.postMessage({ type: 'ando:email-link', href: a.href }, '*');
      } catch (err) { /* swallow */ }
    });
  });
})();
`.trim();

function buildSrcdoc(bodyHtml: string): string {
  // May 24, 2026 fix — ``default-src 'none'`` without an explicit
  // ``script-src`` directive falls back to default and BLOCKS our inline
  // bootstrap script. Symptom: the auto-height + link-tracking never
  // initialise (iframe stays at min-height, links don't post-message)
  // and the browser logs `Refused to execute inline script ... is not a
  // function` against the bootstrap IIFE.
  //
  // The bootstrap is HARD-CODED (never composed from user input — see
  // IFRAME_BOOTSTRAP above), so ``'unsafe-inline'`` on script-src is
  // safe here: no untrusted code path can ever inject an <script>.
  // The sandbox stays null-origin via the iframe's ``sandbox=``
  // attribute, so even a tampered script cannot reach Ando cookies /
  // localStorage / DOM.
  return `<!DOCTYPE html>
<html><head>
<base target="_blank">
<meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src https: data: blob: cid:; style-src 'unsafe-inline'; script-src 'unsafe-inline'; font-src https: data:;">
<style>${ANDO_EMAIL_CSS_RESET}</style>
</head><body>${bodyHtml}<script>${IFRAME_BOOTSTRAP}</script></body></html>`;
}

// ──────────────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────────────

export const EmailHtmlRenderer: React.FC<EmailHtmlRendererProps> = ({
  html,
  loadRemoteImages = false,
  maxHeight,
  className,
  onLinkClick,
  onTrackersDetected,
  inlineImages,
  trackerLabel = (n) => `${n} tracker bloccat${n === 1 ? "o" : "i"}`,
  trackerAriaLabel = (n) =>
    `${n} tracker bloccat${n === 1 ? "o" : "i"} — clicca per dettagli`,
}) => {
  const iframeRef = React.useRef<HTMLIFrameElement | null>(null);

  // Sanitise + tracker stats — memoised so we don't churn DOMPurify on
  // every render. ``inlineImages`` is part of the memo key because a
  // late-arriving blob URL (the parent pre-fetches asynchronously)
  // must trigger a re-sanitise so the body picks up the new src.
  const { html: sanitised, trackerCount, trackerSamples } = React.useMemo(
    () => sanitiseEmail(html, loadRemoteImages, inlineImages),
    [html, loadRemoteImages, inlineImages],
  );

  const srcdoc = React.useMemo(() => buildSrcdoc(sanitised), [sanitised]);

  // Notify caller about trackers (once per unique tracker batch).
  React.useEffect(() => {
    if (trackerCount > 0) {
      onTrackersDetected?.(trackerCount, trackerSamples);
    }
  }, [trackerCount, trackerSamples, onTrackersDetected]);

  // Auto-height + link click handler.
  const [height, setHeight] = React.useState<number>(80);
  const [showTrackerDetails, setShowTrackerDetails] = React.useState(false);

  React.useEffect(() => {
    const handler = (e: MessageEvent) => {
      // Ignore messages not from our iframe.
      if (iframeRef.current && e.source !== iframeRef.current.contentWindow) {
        return;
      }
      const data = e.data as { type?: string; height?: number; href?: string } | null;
      if (!data || typeof data !== "object") return;

      if (data.type === "ando:email-height" && typeof data.height === "number") {
        const cap = maxHeight ?? Number.POSITIVE_INFINITY;
        setHeight(Math.min(data.height, cap));
        return;
      }
      if (data.type === "ando:email-link" && typeof data.href === "string") {
        if (onLinkClick) {
          onLinkClick(data.href);
        } else if (typeof window !== "undefined") {
          window.open(data.href, "_blank", "noopener,noreferrer");
        }
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [maxHeight, onLinkClick]);

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <iframe
        ref={iframeRef}
        title="Email body"
        srcDoc={srcdoc}
        sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
        referrerPolicy="no-referrer"
        // The iframe is `null origin`, so it cannot reach Ando cookies /
        // localStorage / DOM even with allow-scripts. Caller sees only
        // postMessage events that we explicitly emit.
        // L'iframe si estende SEMPRE all'altezza reale del contenuto: lo
        // scroll è quello della pagina, uno solo. Il cap a 80vh creava una
        // seconda area scrollabile annidata — "finestra dentro la finestra",
        // staccata dal resto dell'interfaccia. Chi vuole un tetto lo chiede
        // esplicitamente con ``maxHeight`` (lo fa il fold del thread citato).
        style={{
          width: "100%",
          height: `${height}px`,
          border: "0",
          background: "transparent",
          display: "block",
        }}
      />
      {trackerCount > 0 ? (
        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={() => setShowTrackerDetails((v) => !v)}
            className={cn(
              "inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1",
              "border border-ando-orange/30 bg-ando-orange-soft text-ando-orange",
              "font-medium text-[11px] transition-colors hover:bg-ando-orange/15",
            )}
            aria-expanded={showTrackerDetails}
            aria-label={trackerAriaLabel(trackerCount)}
          >
            <ShieldAlert className="h-3 w-3" aria-hidden="true" />
            {trackerLabel(trackerCount)}
          </button>
          {showTrackerDetails && trackerSamples.length > 0 ? (
            <ul
              className={cn(
                "ml-2 flex flex-col gap-0.5 rounded-md border border-hairline",
                "bg-cream-deep/40 px-2 py-1.5 font-mono text-[10px] text-ink-3",
              )}
            >
              {trackerSamples.map((url) => (
                <li key={url} className="truncate" title={url}>
                  {url}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
EmailHtmlRenderer.displayName = "EmailHtmlRenderer";
